
import React from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { Separator } from '@/components/ui/separator';
import { BasicInfoSectionProps } from './types';
// Point to the standardized validation utilities
import {
  validatePhone,
  validateEmail,
} from '@/components/portfolio/settings/utils/validationUtils';
// formatPhoneNumber and validateWebsite can stay from @/utils/validation if they are suitable
import { formatPhoneNumber, validateWebsite } from '@/utils/validation';


const BasicInfoSection: React.FC<BasicInfoSectionProps & { errors?: { phone?: string; email?: string; website?: string } }> = ({
  businessDetails,
  handleInputChange,
  handleDateSelect,
  selectedDate,
  calendarOpen,
  setCalendarOpen,
  nameExceeds30Chars,
  shortNameLength,
  shortNameManuallyEdited,
  handleWebsiteBlur,
  errors = {}
}) => {
  // Stop propagation of all events to prevent calendar from closing
  const stopPropagation = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    e.preventDefault();
  };

  // Custom handler for phone input to format and validate
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const digitsOnly = value.replace(/\D/g, '');

    // Only allow up to 10 digits
    if (digitsOnly.length > 10) return;

    // Format the phone number as user types
    let formattedValue = value;
    if (digitsOnly.length === 10) {
      formattedValue = formatPhoneNumber(digitsOnly);
    }

    // Update with formatted value
    const syntheticEvent = {
      ...e,
      target: {
        ...e.target,
        name: 'phone',
        value: formattedValue
      }
    };

    handleInputChange(syntheticEvent);

    // Log validation for debugging
    console.log('Phone valid:', validatePhone(formattedValue));
  };

  // Custom handler for website input with auto-formatting
  const handleWebsiteInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const prevValue = businessDetails.website;

    // Log the original value for debugging
    console.log('Website entered:', value);
    console.log('Previous value:', prevValue);

    // Special handling for backspace/delete operations
    // Check if the user is trying to delete part of the protocol
    if ((prevValue.startsWith('https://') || prevValue.startsWith('http://')) &&
      (value.length < prevValue.length) &&
      (prevValue.indexOf(value) === 0 || value === '')) {
      // User is backspacing through the protocol or has deleted everything
      console.log('Allowing protocol deletion');
      handleInputChange(e);
      return;
    }

    // Clean the input by removing leading/trailing whitespace
    let cleanedValue = value.trim();

    // Add protocol immediately if not present
    if (cleanedValue &&
      !cleanedValue.startsWith('http://') &&
      !cleanedValue.startsWith('https://')) {

      // Remove www. prefix if present before adding protocol
      if (cleanedValue.startsWith('www.')) {
        cleanedValue = cleanedValue.substring(4);
      }

      // Only add protocol if there's actual content
      if (cleanedValue.length > 0) {
        cleanedValue = 'https://' + cleanedValue;
      }
    }

    console.log('Formatted website:', cleanedValue);

    // Create a synthetic event with the formatted value (with protocol)
    const syntheticEvent = {
      ...e,
      target: {
        ...e.target,
        name: 'website',
        value: cleanedValue
      }
    };

    handleInputChange(syntheticEvent);
  };

  // Custom handler for short name input to ensure truncation with ellipsis
  const handleShortNameInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    // Log the original value for debugging
    console.log('Short name entered:', value);

    // Manually truncate with ellipsis if needed
    const truncatedValue = value.length > 30
      ? value.substring(0, 27) + "..."
      : value;

    console.log('Truncated value:', truncatedValue);

    // Create a synthetic event with the truncated value
    const syntheticEvent = {
      ...e,
      target: {
        ...e.target,
        name: 'shortName',
        value: truncatedValue // Use the truncated value
      }
    };

    handleInputChange(syntheticEvent);
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <label htmlFor="business-search" className="text-sm font-medium">Business Name</label>
        <Input
          id="name"
          name="name"
          value={businessDetails.name}
          onChange={handleInputChange}
          placeholder="Business Name"
          className="px-4"
        />
      </div>

      {nameExceeds30Chars && (
        <div className="space-y-2">
          <div className="flex justify-between">
            <label htmlFor="shortName" className="text-sm font-medium">Business Short Name</label>
            <span className={`text-xs ${shortNameLength > 30 ? 'text-red-500 font-medium' : 'text-gray-500'}`}>
              {shortNameLength}/30
            </span>
          </div>
          <Input
            id="shortName"
            name="shortName"
            value={
              businessDetails.shortName.length > 30
                ? businessDetails.shortName.substring(0, 27) + "..." // Add ellipsis if truncated
                : businessDetails.shortName
            }
            onChange={handleShortNameInput}
            className={cn(
              shortNameLength > 30 ? "border-red-500 focus-visible:ring-red-500" : "",
              "px-4"
            )}
            placeholder="Short name for display"
            maxLength={27} // Limit to 27 chars to allow for the ellipsis
          />
          <p className="text-xs text-gray-500">
            Provide a shorter version of the business name for display purposes.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label htmlFor="phone" className="text-sm font-medium">Business Phone Number</label>
          <Input
            id="phone"
            name="phone"
            value={businessDetails.phone}
            onChange={handlePhoneChange}
            placeholder="(123) 456-7890"
            className={cn(
              "px-4",
              errors.phone ? "border-red-500 focus-visible:border-red-500" : ""
            )}
          />
          {errors.phone && (
            <p className="text-xs text-red-500 mt-1">{errors.phone}</p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="website" className="text-sm font-medium">Website (Optional)</label>
          <Input
            id="website"
            name="website"
            value={businessDetails.website}
            onChange={handleWebsiteInput}
            onBlur={handleWebsiteBlur}
            placeholder="example.com or https://example.com"
            className={cn(
              "px-4",
              errors.website ? "border-red-500 focus-visible:border-red-500" : ""
            )}
          />
          {errors.website && (
            <p className="text-xs text-red-500 mt-1">{errors.website}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2 col-span-1">
          <label htmlFor="email" className="text-sm font-medium">Business Email (Optional)</label>
          <Input
            id="email"
            name="email"
            type="email"
            value={businessDetails.email}
            onChange={handleInputChange}
            placeholder="business@example.com"
            className={cn(
              "px-4",
              errors.email ? "border-red-500 focus-visible:border-red-500" : ""
            )}
          />
          {errors.email && (
            <p className="text-xs text-red-500 mt-1">{errors.email}</p>
          )}
        </div>

        <div className="space-y-2 col-span-1">
          <label htmlFor="founded" className="text-sm font-medium">Date Founded (Optional)</label>
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal px-4 bg-background focus-visible:outline-none focus-visible:ring-0 focus-visible:border-black border-input hover:bg-white hover:border-input",
                  !selectedDate && "text-muted-foreground"
                )}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {selectedDate ? format(selectedDate, "MMM d, yyyy") : <span>Select Date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-auto p-0 calendar-wrapper"
              align="start"
              onClick={stopPropagation}
              onMouseDown={stopPropagation}
              onPointerDown={stopPropagation}
              onTouchStart={stopPropagation}
              style={{ pointerEvents: 'auto' }}
            >
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={handleDateSelect}
                fromYear={1900}
                toYear={new Date().getFullYear()}
                captionLayout="dropdown-buttons"
                initialFocus
                className="pointer-events-auto calendar-wrapper"
                onDayClick={(day, modifiers, e) => {
                  // Prevent event propagation for day clicks
                  e.stopPropagation();
                }}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </div>
  );
};

export default BasicInfoSection;
